import { Constants, PTB_LEG_TIERS } from '@models/Constants'
import { CountryData } from '@models/Country'
import { Store } from '@stores/Store'

// Path to Bronze pays a flat per-market monthly amount, cumulative by qualifying
// leg count (3..6). It never contributes to annual totals (max 12 payments with
// monthly re-qualification, the summary shows an info tooltip instead), so this
// class deliberately has no annual getter.
export class PathToBronzeCalculation {
  store!: Store

  constructor(store: Store) {
    this.store = store
  }

  // A PTB leg is a frontline with >= 100 group PV. This is NOT the Bronze 3%-level
  // rule (3% = 200 PV in EU markets), so it gets its own count over all frontlines.
  get pathToBronzeLegsCount(): number {
    const { computedFrontlines } = this.store
    const legs = computedFrontlines.filter((frontline) => frontline.groupPV >= Constants.PTB_MIN_LEG_GPV)

    return Math.min(legs.length, Constants.PTB_MAX_LEGS)
  }

  get pathToBronzePpvCriteriaMet(): boolean {
    // Flat 100 PPV requirement for every market, including ANZ
    return this.store.personalPV >= Constants.BRONZE_MIN_ACTUAL_PPV
  }

  get pathToBronzeLegsCriteriaMet(): boolean {
    return this.pathToBronzeLegsCount >= Constants.PTB_MIN_LEGS
  }

  get pathToBronzeAllCriteriaMet(): boolean {
    const { pathToBronzeActive } = this.store.pathToBronzeStore

    return pathToBronzeActive && this.pathToBronzePpvCriteriaMet && this.pathToBronzeLegsCriteriaMet
  }

  pathToBronzeAmountForLegs(legs: number): number {
    const { pathToBronze } = this.store.countryData

    return pathToBronze[`legs${legs}` as keyof CountryData['pathToBronze']] ?? 0
  }

  // Tiers the user may currently select (3 legs up to their qualifying leg count),
  // mirrors the Excel's spilled eligible-amounts dropdown
  get pathToBronzeEligibleLegs(): number[] {
    if (!this.pathToBronzeAllCriteriaMet) { return [] }

    const legsCount = this.pathToBronzeLegsCount

    return PTB_LEG_TIERS.filter((legs) => legs <= legsCount)
  }

  // The tier the payout actually uses. A persisted selection can exceed the
  // current leg count after frontline edits, so it clamps down to the highest
  // tier the structure still qualifies for. null means no payout (nothing
  // selected, or criteria unmet). The section syncs the dropdown to this value
  // so the displayed selection always matches the paid amount.
  get pathToBronzeEffectiveLegs(): number | null {
    const { pathToBronzeSelectedLegs } = this.store.pathToBronzeStore

    if (!this.pathToBronzeAllCriteriaMet || pathToBronzeSelectedLegs === null) { return null }

    return Math.min(pathToBronzeSelectedLegs, this.pathToBronzeLegsCount)
  }

  get pathToBronzeMonthlyAmount(): number {
    const legs = this.pathToBronzeEffectiveLegs

    if (legs === null) { return 0 }

    return this.pathToBronzeAmountForLegs(legs)
  }

}
